/**
 * Regras do pós-venda: implantação, bonificação e comissão.
 *
 * Vivem aqui, e não no serviço, porque o Dashboard precisa explicar os mesmos
 * números que a API calcula. Todo valor monetário circula em centavos, pela
 * razão descrita em `dinheiro.ts`.
 */
import { somarDias } from './cadencia.js';
export { somarDias };
/** Prazo interno da implantação quando a operadora não impõe um próprio. */
export const PRAZO_IMPLANTACAO_DIAS = 7;
/** Mês de vigência em que a revisão preventiva do contrato é disparada. */
export const MES_REVISAO_PREVENTIVA = 9;
/** Mês de vigência em que o contrato chega à renovação. */
export const MES_RENOVACAO = 12;
/** Parcelas mensais geradas para um contrato ativo. */
export const PARCELAS_POR_CONTRATO = 12;
/** Dias de antecedência com que uma pendência vira tarefa automática. */
export const ANTECEDENCIA_TAREFA_DIAS = 2;
// ------------------------------------------------------------ Datas
/**
 * Soma meses preservando o fim de mês: 31/01 + 1 mês é 28/02, não 03/03.
 * A regra importa porque vencimento de boleto cai em dia fixo.
 */
export function somarMeses(data, meses) {
    const resultado = new Date(data);
    const diaOriginal = resultado.getDate();
    resultado.setMonth(resultado.getMonth() + meses);
    if (resultado.getDate() < diaOriginal)
        resultado.setDate(0);
    return resultado;
}
/** Dias inteiros de hoje até a data; negativo quando já passou. */
export function diasAte(data, agora) {
    const alvo = new Date(data);
    alvo.setHours(12, 0, 0, 0);
    const referencia = new Date(agora);
    referencia.setHours(12, 0, 0, 0);
    return Math.round((alvo.getTime() - referencia.getTime()) / 86_400_000);
}
/**
 * Faixas padrão da Reducee. São o ponto de partida do banco, não a verdade:
 * a tabela `faixas_bonificacao` manda, porque as faixas mudam por decisão
 * comercial e o histórico não deve ser reescrito por um deploy.
 */
export const FAIXAS_BONIFICACAO_PADRAO = [
    { valorDe: 0, valorAte: 249_999, percentual: 0 },
    { valorDe: 250_000, valorAte: 499_999, percentual: 10 },
    { valorDe: 500_000, valorAte: 749_999, percentual: 15 },
    { valorDe: 750_000, valorAte: 999_999, percentual: 20 },
    { valorDe: 1_000_000, valorAte: 1_499_999, percentual: 25 },
    { valorDe: 1_500_000, valorAte: null, percentual: 30 },
];
/** Percentual de bonificação para uma Produção paga, em centavos. */
export function percentualDeBonificacao(producaoPagaCentavos, faixas = FAIXAS_BONIFICACAO_PADRAO) {
    const faixa = [...faixas]
        .sort((a, b) => b.valorDe - a.valorDe)
        .find((candidata) => producaoPagaCentavos >= candidata.valorDe);
    return faixa?.percentual ?? 0;
}
/** Valor da bonificação em centavos, arredondado ao centavo. */
export function calcularBonificacao(producaoPagaCentavos, faixas = FAIXAS_BONIFICACAO_PADRAO) {
    const percentual = percentualDeBonificacao(producaoPagaCentavos, faixas);
    return Math.round((producaoPagaCentavos * percentual) / 100);
}
/**
 * Cronograma de comissão de um contrato.
 *
 * Só existe a partir do pagamento do primeiro boleto: é ele que confirma a
 * venda para a operadora, e comissão prevista sobre venda não paga é ficção.
 */
export function preverComissao(valorMensalCentavos, regra, pagamentoPrimeiroEm) {
    const parcela = (percentual) => Math.round((valorMensalCentavos * percentual) / 100);
    if (regra.tipo === 'PARCELA_UNICA') {
        return [
            {
                descricao: 'Parcela única',
                competencia: pagamentoPrimeiroEm,
                valor: parcela(regra.percentual),
                recorrente: false,
            },
        ];
    }
    const total = Math.max(1, regra.parcelas);
    const previstas = [];
    for (let indice = 0; indice < total; indice += 1) {
        previstas.push({
            descricao: `Comissão ${indice + 1}/${total}`,
            competencia: somarMeses(pagamentoPrimeiroEm, indice),
            valor: parcela(regra.percentual),
            recorrente: false,
        });
    }
    if (regra.vitalicio > 0) {
        previstas.push({
            descricao: `Vitalício ${regra.vitalicio}% · próxima`,
            competencia: somarMeses(pagamentoPrimeiroEm, total),
            valor: parcela(regra.vitalicio),
            recorrente: true,
        });
    }
    return previstas;
}
// ---------------------------------------------- Situação de prazo
export const SITUACOES_PRAZO = ['SEM_PRAZO', 'NO_PRAZO', 'PROXIMO', 'ATRASADO'];
/**
 * Classifica um prazo operacional (implantação, movimentação, parcela).
 * "Próximo" usa a mesma antecedência que cria a tarefa automática, para que a
 * tela e a fila de trabalho nunca discordem.
 */
export function avaliarPrazo(prazo, agora) {
    if (!prazo)
        return { situacao: 'SEM_PRAZO', dias: 0 };
    const dias = diasAte(prazo, agora);
    if (dias < 0)
        return { situacao: 'ATRASADO', dias };
    if (dias <= ANTECEDENCIA_TAREFA_DIAS)
        return { situacao: 'PROXIMO', dias };
    return { situacao: 'NO_PRAZO', dias };
}
export const ROTULO_SITUACAO_PRAZO = {
    SEM_PRAZO: 'Sem prazo',
    NO_PRAZO: 'No prazo',
    PROXIMO: 'Próximo do vencimento',
    ATRASADO: 'Atrasado',
};
//# sourceMappingURL=posvenda.js.map